"use client";

import React from "react";
import { PhoneCall, Clock, ShieldCheck, Wrench, CircleCheck as CheckCircle2, MapPin, ArrowRight, Sparkles } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
export default function EllenwoodHero() {
  const shouldReduceMotion = useReducedMotion();
  const trustHighlights = [{
    icon: Clock,
    title: "24/7 Emergency Dispatch",
    detail: "Fast response in Ellenwood, GA"
  }, {
    icon: ShieldCheck,
    title: "Licensed & Insured",
    detail: "Master plumbing technicians"
  }, {
    icon: Wrench,
    title: "Upfront Honest Pricing",
    detail: "No hidden charges or surprise fees"
  }];
  const quickServices = ["Burst & Leaking Pipe Repair", "Emergency Drain Clearing", "Water Heater Replacement", "Sewer Line Diagnostics"];
  return <section id="ellenwoodhero" data-nav="dark" className="relative overflow-hidden bg-primary text-primary-foreground py-16 md:py-24 lg:py-28">
      {/* Background Decorative Overlay Elements */}
      <div className="absolute inset-0 bg-radial-[circle_at_top_right] from-accent/15 via-transparent to-transparent pointer-events-none" />
      <div className="absolute -bottom-24 -left-24 size-96 rounded-full bg-accent/10 blur-3xl pointer-events-none" />

      <div className="container relative mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          
          {/* Main Hero Copy Column */}
          <motion.div className="lg:col-span-7 flex flex-col items-start text-left" initial={shouldReduceMotion ? {
          opacity: 1,
          y: 0
        } : {
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          ease: "easeOut"
        }}>
            <div className="inline-flex items-center gap-2 mb-4">
              <Badge variant="outline" className="bg-accent/20 border-accent text-accent-foreground font-semibold px-3 py-1 text-xs sm:text-sm tracking-wide rounded-full flex items-center gap-1.5">
                <Sparkles className="size-3.5 text-accent" />
                <span>Ellenwood Local Emergency Team</span>
              </Badge>
              <div className="hidden sm:flex items-center gap-1 text-xs text-primary-foreground/80">
                <MapPin className="size-3.5 text-accent" />
                <span>Serving Ellenwood & Clayton County</span>
              </div>
            </div>

            <h1 className="font-default text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight leading-tight mb-4">
              Plumbing Services in <span className="text-accent underline decoration-accent/40 decoration-wavy underline-offset-8">Ellenwood, GA</span>
            </h1>

            <p className="text-lg sm:text-xl text-primary-foreground/90 font-normal max-w-2xl mb-8 leading-relaxed">
              Fast, Reliable Plumbing Help Available 24/7 in Ellenwood. When leaks, clogs, or water heater failures strike, our experienced technicians arrive promptly with complete solutions.
            </p>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 w-full sm:w-auto mb-10">
              <Button asChild size="lg" className="bg-accent text-accent-foreground font-bold hover:bg-accent/90 shadow-lg text-base h-12 px-6 rounded-md transition-transform active:scale-95">
                <Link to="/contact/" className="inline-flex items-center justify-center gap-2">
                  <span>Request Service</span>
                  <ArrowRight className="size-4" />
                </Link>
              </Button>

              <Button asChild variant="outline" size="lg" className="border-accent/40 bg-primary-foreground/10 text-primary-foreground hover:bg-primary-foreground/20 hover:text-primary-foreground font-bold text-base h-12 px-6 rounded-md transition-colors shadow-sm">
                <a href="tel:4708479076" className="inline-flex items-center justify-center gap-2">
                  <PhoneCall className="size-4 text-accent" />
                  <span>Call (470) 847-9076</span>
                </a>
              </Button>
            </div>

            {/* Feature Pills */}
            <div className="w-full pt-6 border-t border-primary-foreground/15 grid grid-cols-1 sm:grid-cols-2 gap-3">
              {quickServices.map((service, index) => <div key={service} data-index={index} className="flex items-center gap-2 text-sm text-primary-foreground/90">
                  <CheckCircle2 className="size-4 text-accent shrink-0" />
                  <span>{service}</span>
                </div>)}
            </div>
          </motion.div>

          {/* Visual Showcase / Emergency Card Column */}
          <motion.div className="lg:col-span-5 flex flex-col gap-4" initial={shouldReduceMotion ? {
          opacity: 1,
          scale: 1
        } : {
          opacity: 0,
          scale: 0.96
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.6,
          delay: 0.15,
          ease: "easeOut"
        }}>
            <div className="relative rounded-2xl overflow-hidden shadow-2xl border border-primary-foreground/20">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/plumber_repair_ellenwood_hero.webp" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/plumber_repair_ellenwood_hero.webp-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/plumber_repair_ellenwood_hero.webp-wvc-sizes" alt="Professional emergency plumber working on pipes in Ellenwood GA" className="w-full h-72 sm:h-80 object-cover object-center" loading="lazy" decoding="async" />
              <div className="absolute inset-0 bg-gradient-to-t from-primary via-primary/40 to-transparent" />
              
              <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between gap-2 p-3.5 rounded-xl bg-background/90 text-foreground backdrop-blur-md shadow-md border border-border">
                <div className="flex items-center gap-3">
                  <div className="size-10 rounded-full bg-accent/20 flex items-center justify-center text-accent">
                    <PhoneCall className="size-5 text-accent-foreground" />
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Direct Ellenwood Line</p>
                    <p className="text-sm font-bold text-foreground">(470) 847-9076</p>
                  </div>
                </div>
                <Badge variant="secondary" className="bg-accent text-accent-foreground font-bold text-xs py-1 px-2.5">
                  Live Dispatch
                </Badge>
              </div>
            </div>

            {/* Value Props Card Matrix */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {trustHighlights.map((item, index) => {
              const ItemIcon = item.icon;
              return <Card key={item.title} data-index={index} className="bg-primary-foreground/10 border-primary-foreground/15 text-primary-foreground p-3.5 gap-2 rounded-xl shadow-xs">
                    <CardContent className="p-0 flex flex-col gap-1.5">
                      <div className="size-8 rounded-lg bg-accent/20 flex items-center justify-center text-accent">
                        <ItemIcon className="size-4 text-accent" />
                      </div>
                      <p className="text-xs font-bold leading-tight text-primary-foreground">{item.title}</p>
                      <p className="text-[11px] text-primary-foreground/75 leading-snug">{item.detail}</p>
                    </CardContent>
                  </Card>;
            })}
            </div>
          </motion.div>

        </div>
      </div>
    </section>;
}